import { PaginatedResponse } from "@/interfaces/paginatedResponse";
import axios from "@/lib/axios";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { create } from "lodash";
import { Prop, ref } from "vue";
import { ToastInterface, useToast } from "vue-toastification";
import { Brand } from "../../../../interfaces/Brand";

interface Props {
  immediate?: boolean;
}

interface Params {
  page: number;
}

interface CreateParams {
  name: string;
}

interface CreateMediaParams {
  id: number | string;
  media: File;
  collection: string;
}

interface UpdateParams {
  id: number;
  name?: string;
  chainId: number | null;
}

const getBrands = async (params: Params): Promise<PaginatedResponse<Brand>> => {
  const { data } = await axios.get(`/api/internal/brands?page=${params.page}`);

  return data;
};

const createBrand = async (params: CreateParams) => {
  const { data } = await axios.post(`/api/internal/brands`, params);

  return data;
};

const createBrandMedia = async (params: CreateMediaParams) => {
  const formData = new FormData();
  formData.append("media", params.media);
  formData.append("collection", params.collection);

  const { data } = await axios.post(
    `/api/internal/brands/${params.id}/medias`,
    formData,
  );

  return data;
};

const updateBrand = async (params: UpdateParams, toast: ToastInterface) => {
  try {
    const { data } = await axios.put(`/api/internal/brands/${params.id}`, {
      name: params.name,
      chain_id: params.chainId,
    });

    toast.success("Marca actualizada correctamente");

    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const deleteBrand = async (id: number, toast: ToastInterface) => {
  try {
    const { data } = await axios.delete(`/api/internal/brands/${id}`);

    toast.success("Marca desactivada correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const restoreBrand = async (id: number, toast: ToastInterface) => {
  try {
    const { data } = await axios.post(`/api/internal/brands/${id}/restore`);

    toast.success("Marca activada correctamente");
    return data;
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useBrands = (props: Props) => {
  const { immediate = true } = props;
  const toast = useToast();
  const page = ref<number | string>(1);

  const { data, isLoading, refetch } = useQuery(
    ["brands"],
    () => getBrands({ page: Number(page.value) }),
    {
      enabled: immediate,
    },
  );

  const createMutation = useMutation((params: CreateParams) =>
    createBrand(params),
  );

  const createMediaMutation = useMutation((params: CreateMediaParams) =>
    createBrandMedia(params),
  );

  const updateMutation = useMutation((params: UpdateParams) =>
    updateBrand(params, toast),
  );

  const deleteMutation = useMutation((id: number) => deleteBrand(id, toast));

  const restoreMutation = useMutation((id: number) => restoreBrand(id, toast));

  return {
    data,
    isLoading,
    refetch,
    page,

    createMutation,
    createMutationState: createMutation,
    createMutationLoading: createMutation.isLoading,
    createMutationError: createMutation.isError,
    createMutationSuccess: createMutation.isSuccess,
    createMutationMutate: createMutation.mutate,
    createMutationAsync: createMutation.mutateAsync,

    createMediaMutation,
    createMediaMutationState: createMediaMutation,
    createMediaMutationLoading: createMediaMutation.isLoading,
    createMediaMutationError: createMediaMutation.isError,
    createMediaMutationSuccess: createMediaMutation.isSuccess,
    createMediaMutationMutate: createMediaMutation.mutate,
    createMediaMutationAsync: createMediaMutation.mutateAsync,

    updateMutation,
    updateMutationState: updateMutation,
    updateMutationLoading: updateMutation.isLoading,
    updateMutationError: updateMutation.isError,
    updateMutationSuccess: updateMutation.isSuccess,
    updateMutationMutate: updateMutation.mutate,
    updateMutationAsync: updateMutation.mutateAsync,

    deleteMutation,
    deleteMutationState: deleteMutation,
    deleteMutationLoading: deleteMutation.isLoading,
    deleteMutationError: deleteMutation.isError,
    deleteMutationSuccess: deleteMutation.isSuccess,
    deleteMutationMutate: deleteMutation.mutate,
    deleteMutationAsync: deleteMutation.mutateAsync,

    restoreMutation,
    restoreMutationState: restoreMutation,
    restoreMutationLoading: restoreMutation.isLoading,
    restoreMutationError: restoreMutation.isError,
    restoreMutationSuccess: restoreMutation.isSuccess,
    restoreMutationMutate: restoreMutation.mutate,
    restoreMutationAsync: restoreMutation.mutateAsync,
  };
};

export default useBrands;
